import React, { RefObject, useCallback, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import styled from 'styled-components';

import Button from '../../../components/button/Button';
import { quantizeToFrame } from '../../../utils/timecode';
import MaEditDrawer from '../ma/MaEditDrawer';
import { MaOptionSlot } from '../ma/maTypes';
import { MaVariantGroup } from '../ma/maVariantGroups';
import { TimelineTrackData } from './TimelinePanel';
import {
  resolveClipDuration,
  resolveClipSourceDuration,
  TimelineClipView,
} from './WaveformTrack';

/**
 * タイムライン編集モードのオーバーレイ群。
 *
 * - MaEditClipLayer: オーディオクリップの選択枠 + 末尾トリムハンドル
 * - MaEditDropLanes: ドロワーからの D&D 受け入れレーン（既存トラック + 新規トラック）
 * - MaEditFooter: 下ドロワー（オーディオタグ）+ 更新 / キャンセル
 */

// ===== 編集モードのプロパティ（TimelinePanel から受け渡し） =====

export interface TimelineMaEditProps {
  /** 編集アイコンを表示するか（権限 + MA スライド表示中） */
  available: boolean;
  isEditing: boolean;
  isDirty: boolean;
  isSaving: boolean;
  selectedClipId: string | null;
  variantGroups: MaVariantGroup[];
  variantOverrides: Record<string, string>;
  /** 編集中セグメントのオプションスロット（`(x)` / `[x]` マーカー） */
  optionSlots: MaOptionSlot[];
  optionSelections: Record<string, string>;
  onEnterEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onSelectClip: (_id: string | null) => void;
  onTrimClip: (_id: string, _durationSeconds: number) => void;
  onAddClip: (_group: MaVariantGroup, _startSeconds: number, _track: number) => void;
  onSwitchVariant: (_groupKey: string, _variationName: string) => void;
  onSelectOption: (_slotKey: string, _choiceKey: string) => void;
}

// ===== クリップ選択 + トリムオーバーレイ =====

const ClipBox = styled.div<{ $selected: boolean }>`
  position: absolute;
  z-index: 5;
  border: ${p =>
    p.$selected
      ? '2px solid var(--soremo-blue, #009ace)'
      : '1px solid transparent'};
  border-radius: 2px;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    border-color: var(--soremo-blue, #009ace);
  }
`;

const TrimHandle = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 0;
  right: -1px;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  border-radius: 0 2px 2px 0;
  background: ${p => (p.$selected ? 'var(--soremo-blue, #009ace)' : 'transparent')};
  opacity: ${p => (p.$selected ? 0.9 : 0)};

  ${ClipBox}:hover & {
    opacity: 0.9;
    background: var(--soremo-blue, #009ace);
  }
`;

function EditableClip({
  clip,
  top,
  height,
  pixelsPerSecond,
  fps,
  selected,
  contentRef,
  onSelect,
  onTrim,
}: {
  clip: TimelineClipView;
  top: number;
  height: number;
  pixelsPerSecond: number;
  fps: number;
  selected: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
  onSelect: (_id: string) => void;
  onTrim: (_id: string, _durationSeconds: number) => void;
}) {
  const editId = clip.editId;
  const trimmingRef = useRef(false);

  const handleBoxPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // 下のスクラブハンドラへ伝播させない（クリップ選択を優先）
      e.stopPropagation();
      if (editId) onSelect(editId);
    },
    [editId, onSelect],
  );

  const trimTo = useCallback(
    (clientX: number) => {
      const content = contentRef.current;
      if (!content || !editId || pixelsPerSecond <= 0) return;
      const rect = content.getBoundingClientRect();
      const rawDuration = (clientX - rect.left) / pixelsPerSecond - clip.startSeconds;
      const sourceDuration = resolveClipSourceDuration(clip);
      const clamped = Math.min(
        Math.max(1 / fps, quantizeToFrame(rawDuration, fps)),
        sourceDuration ?? Infinity,
      );
      onTrim(editId, clamped);
    },
    [contentRef, editId, pixelsPerSecond, clip, fps, onTrim],
  );

  const handleTrimPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!editId) return;
      onSelect(editId);
      trimmingRef.current = true;
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    },
    [editId, onSelect],
  );

  const handleTrimPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!trimmingRef.current) return;
      trimTo(e.clientX);
    },
    [trimTo],
  );

  const handleTrimPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!trimmingRef.current) return;
    trimmingRef.current = false;
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
  }, []);

  if (!editId) return null;
  const duration = resolveClipDuration(clip);
  const width = Math.max(4, duration * pixelsPerSecond);
  const left = clip.startSeconds * pixelsPerSecond;

  return (
    <ClipBox
      $selected={selected}
      style={{ top, left, width, height }}
      onPointerDown={handleBoxPointerDown}
    >
      {/* オプション候補由来のクリップは候補の配置が正のため編集不可（ハンドル非表示） */}
      {!clip.optionSlotKey && (
        <TrimHandle
          $selected={selected}
          onPointerDown={handleTrimPointerDown}
          onPointerMove={handleTrimPointerMove}
          onPointerUp={handleTrimPointerUp}
          onPointerCancel={handleTrimPointerUp}
        />
      )}
    </ClipBox>
  );
}

export function MaEditClipLayer({
  tracks,
  topOffset,
  trackHeight,
  pixelsPerSecond,
  fps,
  selectedClipId,
  contentRef,
  onSelectClip,
  onTrimClip,
}: {
  tracks: TimelineTrackData[];
  /** ルーラー + ピンレーンの高さ（トラック領域の開始 y） */
  topOffset: number;
  trackHeight: number;
  pixelsPerSecond: number;
  fps: number;
  selectedClipId: string | null;
  contentRef: RefObject<HTMLDivElement | null>;
  onSelectClip: (_id: string | null) => void;
  onTrimClip: (_id: string, _durationSeconds: number) => void;
}) {
  return (
    <>
      {tracks.map((track, trackIdx) => {
        if (track.trackNo == null) return null; // MOV トラックは編集対象外
        const top = topOffset + trackHeight * trackIdx;
        return track.clips.map(clip =>
          clip.editId ? (
            <EditableClip
              key={clip.key}
              clip={clip}
              top={top}
              height={trackHeight}
              pixelsPerSecond={pixelsPerSecond}
              fps={fps}
              selected={clip.editId === selectedClipId}
              contentRef={contentRef}
              onSelect={onSelectClip}
              onTrim={onTrimClip}
            />
          ) : null,
        );
      })}
    </>
  );
}

// ===== ドロップレーン（D&D 受け入れ） =====

const LaneHighlight = styled.div<{ $isOver: boolean; $ghost: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  z-index: 4;
  pointer-events: none;
  box-sizing: border-box;
  background: ${p => (p.$isOver ? 'rgba(0, 154, 206, 0.10)' : 'transparent')};
  border: ${p =>
    p.$isOver
      ? '1px dashed var(--soremo-blue, #009ace)'
      : p.$ghost
        ? '1px dashed var(--soremo-border, #d9dcde)'
        : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

function DropLane({
  trackNo,
  top,
  height,
  ghost,
}: {
  trackNo: number;
  top: number;
  height: number;
  ghost: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `ma-lane-${trackNo}`,
    data: { trackNo },
  });
  return (
    <LaneHighlight ref={setNodeRef} style={{ top, height }} $isOver={isOver} $ghost={ghost}>
      {ghost && (
        <span className="bodytext-11 u-text-light-gray">
          ここにドロップで新規トラック（A{trackNo}）
        </span>
      )}
    </LaneHighlight>
  );
}

export function MaEditDropLanes({
  tracks,
  topOffset,
  trackHeight,
}: {
  tracks: TimelineTrackData[];
  topOffset: number;
  trackHeight: number;
}) {
  const audioTrackNos = tracks
    .map(t => t.trackNo)
    .filter((n): n is number => n != null);
  const maxTrackNo = audioTrackNos.length > 0 ? Math.max(...audioTrackNos) : 0;

  return (
    <>
      {tracks.map((track, trackIdx) =>
        track.trackNo != null ? (
          <DropLane
            key={`lane-${track.trackNo}`}
            trackNo={track.trackNo}
            top={topOffset + trackHeight * trackIdx}
            height={trackHeight}
            ghost={false}
          />
        ) : null,
      )}
      {/* 最下段: 新規トラック追加レーン */}
      <DropLane
        trackNo={maxTrackNo + 1}
        top={topOffset + trackHeight * tracks.length}
        height={trackHeight}
        ghost
      />
    </>
  );
}

// ===== 編集フッター（ドロワー + 更新 / キャンセル） =====

const FooterContainer = styled.div`
  border-top: 1px solid var(--soremo-border);
  padding: 8px 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export function MaEditFooter({ maEdit }: { maEdit: TimelineMaEditProps }) {
  return (
    <FooterContainer>
      <MaEditDrawer
        groups={maEdit.variantGroups}
        overrides={maEdit.variantOverrides}
        onSwitchVariant={maEdit.onSwitchVariant}
        optionSlots={maEdit.optionSlots}
        optionSelections={maEdit.optionSelections}
        onSelectOption={maEdit.onSelectOption}
        disabled={maEdit.isSaving}
      />
      <FooterActions>
        <span className="bodytext-11 u-text-light-gray">
          クリップ選択後 ←→ でナッジ（SHIFT で1秒）/ Delete で削除 / 右端ドラッグで終了位置を調整
        </span>
        <ActionButtons>
          <Button
            theme="secondary"
            variant="outlined"
            size="small"
            width="fit"
            onClick={maEdit.onCancel}
            disabled={maEdit.isSaving}
          >
            キャンセル
          </Button>
          <Button
            theme="primary"
            variant="filled"
            size="small"
            width="fit"
            onClick={maEdit.onSave}
            disabled={!maEdit.isDirty || maEdit.isSaving}
          >
            {maEdit.isSaving ? '保存中...' : '更新'}
          </Button>
        </ActionButtons>
      </FooterActions>
    </FooterContainer>
  );
}
